import { FunctionComponent, ReactNode, useCallback } from "react";

import "./ActionButton.css";

const ActionButton: FunctionComponent<ActionButtonProps> = ({
  children,
  testID,
  disabled = false,
  onPress,
  href,
  icon,
  target,
  label,
}) => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (onPress && !disabled) {
        onPress(event);
      }
    },
    [onPress, disabled],
  );

  return (
    <a
      data-testid={testID}
      className="action-button-component"
      href={href}
      role="button"
      aria-label={label}
      rel="noopener"
      onClick={handleClick}
      target={target}>
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </a>
  );
};

interface ActionButtonProps {
  /**
   * Disable clicking the button
   */
  disabled?: boolean;
  /**
   * Test ID for selection in automated testing, sets `data-testid` attribute
   */
  testID?: string;
  /**
   * The button text
   */
  children?: string | React.ReactNode;
  /**
   * Callback when button is pressed
   */
  onPress?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * Location to open when button is pressed
   */
  href?: string;
  /**
   * Target window to open href in.
   */
  target?: string;
  /**
   * Icon of the button
   */
  icon?: ReactNode;
  /**
   * Accessability label.
   */
  label?: string;
}

export { ActionButton, ActionButtonProps };
